// /rapportvakt/utgivare and /rapportvakt/utgivare/:slug — the explorative layer.
//
// The app is behind a pick and a mail address; these are not. Someone searching
// "Boverket rapporter" or "HSB bostadsrättsbarometern" should be able to land on
// a page that says what the publisher is, what of theirs we watch, how often it
// comes and what a delivery looks like, without touching the product first.
//
// Everything on the page comes from src/config/rapportvakt.js. Nothing here
// characterises a publisher beyond the one factual sentence stored with it.

import React, { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import RapportvaktShell, { useRvLang } from '../components/rapportvakt/Shell';
import { Glyph, PublisherMark } from '../components/rapportvakt/Delivery';
import Sheet, { SheetClose } from '../components/rapportvakt/Sheet';
import {
  RAPPORTVAKT_CHANNEL_LABELS,
  RAPPORTVAKT_CHANNEL_LABELS_EN,
  RAPPORTVAKT_MONTHS,
  RAPPORTVAKT_MONTHS_EN,
  RAPPORTVAKT_PUBLISHERS,
  RAPPORTVAKT_PUBLISHERS_PATH,
  RAPPORTVAKT_PUBLISHER_COUNT,
  RAPPORTVAKT_SOURCE_COUNT,
  cadenceMonths,
  deliveriesForPublisher,
  digestsForPublisher,
  getPublisherBySlug,
  rapportvaktDigestPath,
  rapportvaktPublisherPath,
  rapportvaktSamplePath,
  rapportvaktTabPath,
  seriesForPublisher,
} from '../config/rapportvakt';
import {
  getRapportvaktPublisherSeo,
  getRapportvaktPublishersSeo,
} from '../config/seoRoutes';

/** Host only, so a reader can see where an outbound link goes before clicking. */
function linkHost(url) {
  try {
    return new URL(url).host.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function channelLabel(series, lang) {
  const labels = lang === 'en' ? RAPPORTVAKT_CHANNEL_LABELS_EN : RAPPORTVAKT_CHANNEL_LABELS;
  return labels[series.channel];
}

/**
 * Twelve cells, one per month, with the months the cadence names filled in.
 *
 * The cells carry no letters: a strip of twelve initials next to a sentence
 * that already says "Årligen, juni" is the same fact printed twice, and the one
 * that would have to be set in tiny capitals. Each cell names its month to a
 * pointer and to a screen reader instead, and the whole strip is one image with
 * one label so a reader does not tab through twelve squares.
 *
 * Renders nothing when the cadence names no month. That is the point: a series
 * that says "Kvartalsvis" has told us how often, never when, and this component
 * does not fill that in.
 */
function YearStrip({ cadence, lang }) {
  const months = cadenceMonths(cadence);
  if (!months) return null;

  const names = lang === 'en' ? RAPPORTVAKT_MONTHS_EN : RAPPORTVAKT_MONTHS;
  const label = `${lang === 'en' ? 'Release months' : 'Utgivningsmånader'}: ${months
    .map((index) => names[index])
    .join(', ')}`;

  return (
    <span className="rv-year" role="img" aria-label={label}>
      {/* Keyed by position, not by name: the twelve cells are the same twelve
          cells in either language, so switching language relabels them rather
          than replacing them and replaying the animation. */}
      {names.map((name, index) => (
        <span
          className={`rv-year__cell${months.includes(index) ? ' rv-year__cell--on' : ''}`}
          key={index}
          title={name}
          aria-hidden="true"
          style={{ animationDelay: `${index * 35}ms` }}
        />
      ))}
    </span>
  );
}

/* --- index ---------------------------------------------------------------- */

/**
 * One publisher, previewed without leaving the index.
 *
 * Everything the full page opens with, minus the CTA: who they are, what of
 * theirs is watched and how often, and any delivery we have published from
 * them. The full page is one link away and stays the canonical version.
 */
function PublisherSheet({ publisher, onClose }) {
  const { t, lang } = useRvLang();
  const series = seriesForPublisher(publisher);
  const deliveries = deliveriesForPublisher(publisher);

  return (
    <Sheet labelledBy="rv-pubsheet-title" onClose={onClose}>
      <div className="rv-sheet__top">
        <PublisherMark publisher={publisher.name} />
        <div>
          <h2 className="rv-sheet__title" id="rv-pubsheet-title">
            {publisher.name}
          </h2>
          <p className="rv-sheet__sub">{publisher.description}</p>
        </div>
        <SheetClose label={t.close} onClose={onClose} />
      </div>

      <h3 className="rv-pubsheet__head">{t.publisherSeriesHead}</h3>
      <ul className="rv-pubsheet__list">
        {series.map((item) => (
          <li className="rv-pubsheet__item" key={item.id}>
            <span className="rv-pubsheet__name">{item.report}</span>
            <span className="rv-pubsheet__meta">
              {item.cadence}. {channelLabel(item, lang)}
            </span>
          </li>
        ))}
      </ul>

      {deliveries.length ? (
        <>
          <h3 className="rv-pubsheet__head">{t.publisherSamplesHead}</h3>
          <ul className="rv-pubsheet__list">
            {deliveries.map((delivery) => (
              <li className="rv-pubsheet__item" key={delivery.slug}>
                <Link to={rapportvaktSamplePath(delivery.slug)}>{delivery.title}</Link>
                <span className="rv-pubsheet__meta">{delivery.dateLabel}</span>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <div className="rv-sheet__actions rv-pubsheet__actions">
        <Link
          className="rv-btn rv-btn--primary"
          to={rapportvaktPublisherPath(publisher.slug)}
        >
          {t.openPage}
          <Glyph name="arrow" size={13} />
        </Link>
      </div>
    </Sheet>
  );
}

function PublishersIndexBody() {
  const { t } = useRvLang();
  const [openSlug, setOpenSlug] = useState(null);
  const open = openSlug ? getPublisherBySlug(openSlug) : null;

  return (
    <main className="rv__inner">
      <div className="rv-pubs">
        <h1 className="rv-pubs__title">{t.publishersTitle}</h1>
        <p className="rv-pubs__intro">
          {t.publishersIntro(RAPPORTVAKT_PUBLISHER_COUNT, RAPPORTVAKT_SOURCE_COUNT)}
        </p>

        {/* A real link with a real href, intercepted. The cell opens the sheet
            for a reader, but the index still ships fifteen crawlable links to
            the publisher pages, and a middle click or cmd-click still opens one
            the way a link is expected to. */}
        <div className="rv-pubs__grid">
          {RAPPORTVAKT_PUBLISHERS.map((publisher) => (
            <Link
              className="rv-pubcard"
              key={publisher.slug}
              to={rapportvaktPublisherPath(publisher.slug)}
              onClick={(event) => {
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                event.preventDefault();
                setOpenSlug(publisher.slug);
              }}
            >
              <PublisherMark publisher={publisher.name} />
              <span className="rv-pubcard__name">{publisher.name}</span>
            </Link>
          ))}
        </div>
      </div>

      {open ? <PublisherSheet publisher={open} onClose={() => setOpenSlug(null)} /> : null}
    </main>
  );
}

export function RapportvaktPublishersPage() {
  return (
    <RapportvaktShell seo={getRapportvaktPublishersSeo()}>
      <PublishersIndexBody />
    </RapportvaktShell>
  );
}

/* --- one publisher -------------------------------------------------------- */

/**
 * One publisher, as one card in a narrow column.
 *
 * Everything a reader came for is inside the same card, in the order they ask
 * for it: who this is, where their reports live, and what of theirs is watched.
 * Laid out like the help page rather than like a dashboard, because the page is
 * one short answer and a full-width three-section layout made it look like more
 * than it is.
 */
function PublisherBody({ publisher }) {
  const { t, lang } = useRvLang();
  const series = seriesForPublisher(publisher);
  const deliveries = deliveriesForPublisher(publisher);
  const digests = digestsForPublisher(publisher);

  return (
    <main className="rv__inner">
      <div className="rv-pub">
        <p className="rv-pub__back">
          <Link to={RAPPORTVAKT_PUBLISHERS_PATH}>{t.allPublishers}</Link>
        </p>

        <article className="rv-pub__card">
          <header className="rv-pub__head">
            <PublisherMark publisher={publisher.name} />
            <div className="rv-pub__ident">
              <h1 className="rv-pub__name">{publisher.name}</h1>
              <p className="rv-pub__desc">{publisher.description}</p>
              <a
                className="rv-pub__site"
                href={publisher.publicationUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t.reportPage}
                <span className="rv-pub__host">{linkHost(publisher.publicationUrl)}</span>
              </a>
            </div>
          </header>

          <section className="rv-pub__section" aria-labelledby="rv-pub-series">
            <h2 className="rv-pub__head2" id="rv-pub-series">
              {t.publisherSeriesHead}
            </h2>
            <ul className="rv-pub__list">
              {series.map((item) => (
                <li className="rv-series" key={item.id}>
                  <div className="rv-series__name">{item.report}</div>
                  {/* The cadence and the channel are facts about the series, so
                      they are set as text a reader can read, not as a whisper
                      under the title. */}
                  <div className="rv-series__meta">
                    {item.cadence}. {channelLabel(item, lang)}
                  </div>
                  <YearStrip cadence={item.cadence} lang={lang} />
                </li>
              ))}
            </ul>
          </section>

          {/* Weekly (or, later, daily/monthly) automated digests, e.g. SCB's
              /scb/vecka-N-YYYY/ pages — a different kind of content from the
              hand-authored sample deliveries below, see rapportvakt.js'
              "Digests" section. Renders nothing when the publisher has none
              yet, which is every publisher except SCB today. */}
          {digests.length ? (
            <section className="rv-pub__section" aria-labelledby="rv-pub-digests">
              <h2 className="rv-pub__head2" id="rv-pub-digests">
                Senaste veckodigester
              </h2>
              <ul className="rv-pub__list">
                {digests.map((digest) => (
                  <li key={digest.slug}>
                    <Link
                      className="rv-series rv-series--link"
                      to={rapportvaktDigestPath(digest)}
                    >
                      <span className="rv-series__text">
                        <span className="rv-series__name">{digest.title}</span>
                        {digest.dateLabel ? (
                          <span className="rv-series__meta">{digest.dateLabel}</span>
                        ) : null}
                      </span>
                      <Glyph name="arrow" size={14} />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {deliveries.length ? (
            <section className="rv-pub__section" aria-labelledby="rv-pub-samples">
              <h2 className="rv-pub__head2" id="rv-pub-samples">
                {t.publisherSamplesHead}
              </h2>
              <ul className="rv-pub__list">
                {deliveries.map((delivery) => (
                  <li key={delivery.slug}>
                    <Link
                      className="rv-series rv-series--link"
                      to={rapportvaktSamplePath(delivery.slug)}
                    >
                      <span className="rv-series__text">
                        <span className="rv-series__name">{delivery.title}</span>
                        <span className="rv-series__meta">{delivery.dateLabel}</span>
                      </span>
                      <Glyph name="arrow" size={14} />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </article>

        <div className="rv-cta">
          <div>
            <h2 className="rv-cta__title">{t.closeTitle}</h2>
            <p className="rv-cta__sub">{t.publisherPitch}</p>
          </div>
          <Link className="rv-btn rv-btn--primary" to={rapportvaktTabPath('bevakningar')}>
            {t.publisherCta}
            <Glyph name="arrow" size={13} />
          </Link>
        </div>
      </div>
    </main>
  );
}

export default function RapportvaktPublisherPage() {
  const { slug } = useParams();
  const publisher = getPublisherBySlug(slug);

  // An unknown slug is not a publisher. Send it to the index rather than
  // rendering a page that would then need a canonical it cannot have.
  if (!publisher) return <Navigate to={RAPPORTVAKT_PUBLISHERS_PATH} replace />;

  return (
    <RapportvaktShell seo={getRapportvaktPublisherSeo(publisher)}>
      <PublisherBody publisher={publisher} />
    </RapportvaktShell>
  );
}
